import type { ApiClient } from '../../../api/client';
import type { CollectionInputs } from '@ayumia/component-runtime';

/**
 * Sources de dossiers du composant de collection (ADR-149 §4, §6). Une source lit un dossier — son
 * chemin, ses sous-dossiers, ses éléments — et range par les commandes **déclarées** de la vue ; le
 * serveur autorise, valide et refuse. Deux adaptateurs, une seule interface :
 * - `queryFolderSource` lit une **requête déclarée** (`listing_query`, la médiathèque : `ListMedia`) ;
 * - `objectFolderSource` lit l'Object API (type du dossier, champ parent, champ « dossier » de l'élément).
 * Aucun n'est une base ni un cache : chaque lecture repart du serveur.
 */

export type FolderSpec = NonNullable<CollectionInputs['folders']>;

export interface FolderEntry {
  id: string;
  name: string;
  /** Dossier verrouillé (dossier système) : ni renommé, ni déplacé, ni archivé. */
  locked: boolean;
  /** Rien ne s'y range. */
  readonly: boolean;
  description?: string;
  item_count?: number;
  folder_count?: number;
  created_at?: string;
  raw: Record<string, unknown>;
}

export interface FolderNode {
  id: string;
  name: string;
  parent_id: string | null;
  locked: boolean;
}

export interface ListingRequest {
  folderId: string | null;
  q: string;
  /** Portée d'une recherche (avec `q`) : le dossier et ses sous-dossiers, ou partout. */
  scope: 'folder' | 'everywhere';
  /** Sans recherche, toute la source (raccourci « Récents », filtre de format…), aucun dossier. */
  all?: boolean;
  sort?: string;
  /** Filtres d'égalité (puces, raccourcis), par champ. */
  filters: Readonly<Record<string, string>>;
}

export interface Listing<Item> {
  folder: FolderEntry | null;
  path: Array<{ id: string; name: string }>;
  folders: FolderEntry[];
  items: Item[];
  total: number;
  hasMore: boolean;
}

export interface FolderSource<Item> {
  list(request: ListingRequest): Promise<Listing<Item>>;
  /** Tous les dossiers actifs de la source (arborescence, « Déplacer vers… »). */
  allFolders(): Promise<FolderNode[]>;
  /**
   * Charge utile d'une commande à identifiants (rangement, archivage) : les identifiants choisis, ou
   * l'ensemble filtré à résoudre au serveur avec le compte vu (« Sélectionner les N »).
   */
  itemsPayload(target: { ids: readonly string[] } | { request: ListingRequest; total: number }): Record<string, unknown>;
  /** L'ensemble filtré se résout-il au serveur pour une commande à identifiants ? */
  resolvesSelection: boolean;
}

const string = (value: unknown): string | undefined => (typeof value === 'string' ? value : value === null || value === undefined ? undefined : String(value));

function folderEntry(raw: Record<string, unknown>, spec: FolderSpec): FolderEntry {
  const name = string(raw[spec.name_field ?? 'name']) ?? '';
  const lockedBy = spec.locked_field === undefined ? undefined : raw[spec.locked_field];
  const count = (key: string): number | undefined => (typeof raw[key] === 'number' ? (raw[key] as number) : undefined);
  const itemCount = count('file_count') ?? count('item_count');
  const folderCount = count('folder_count');
  return {
    id: String(raw['id']),
    name,
    locked: lockedBy !== undefined && lockedBy !== null && lockedBy !== '',
    readonly: spec.readonly_field !== undefined && raw[spec.readonly_field] === true,
    ...(typeof raw['description'] === 'string' ? { description: raw['description'] } : {}),
    ...(itemCount === undefined ? {} : { item_count: itemCount }),
    ...(folderCount === undefined ? {} : { folder_count: folderCount }),
    ...(typeof raw['created_at'] === 'string' ? { created_at: raw['created_at'] } : {}),
    raw,
  };
}

/** Paramètres d'une lecture par la requête déclarée : fixes, dossier, recherche, portée, tri, filtres déclarés. */
export function listingParameters(spec: FolderSpec, request: ListingRequest): Record<string, string> {
  const declared = spec.listing_query!;
  const parameters: Record<string, string> = { ...(declared.parameters ?? {}) };
  if (request.folderId !== null) parameters['folder_id'] = request.folderId;
  if (request.q !== '') parameters['q'] = request.q;
  // La portée ne part que si elle change la lecture : une recherche, ou « partout » demandé sans dossier.
  if (request.q !== '' && request.folderId !== null) parameters['scope'] = request.scope;
  else if (request.all === true) parameters['scope'] = 'everywhere';
  const sort = request.sort ?? declared.default_sort;
  if (sort !== undefined) parameters['sort'] = sort;
  for (const field of declared.filter_parameters ?? []) {
    const value = request.filters[field];
    if (value !== undefined && value !== '') parameters[field] = value;
  }
  return parameters;
}

/** Adaptateur de la requête déclarée (ADR-149 §6) : la requête rend `{ folder, path, folders, <items_key>, page }`. */
export function queryFolderSource<Item>(client: ApiClient, spec: FolderSpec): FolderSource<Item> {
  const declared = spec.listing_query!;
  const idsField = spec.commands?.move_items?.ids_field ?? 'ids';
  return {
    resolvesSelection: true,
    async list(request) {
      const parameters = { ...listingParameters(spec, request), limit: String(declared.page_size ?? 200) };
      const response = await client.query<Record<string, unknown>>(declared.query_name, parameters);
      const data = response.data;
      const page = (data['page'] ?? {}) as { total?: number; has_more?: boolean };
      const items = (data[declared.items_key] ?? []) as Item[];
      return {
        folder: data['folder'] === null || data['folder'] === undefined ? null : folderEntry(data['folder'] as Record<string, unknown>, spec),
        path: (data['path'] ?? []) as Array<{ id: string; name: string }>,
        folders: ((data['folders'] ?? []) as Array<Record<string, unknown>>).map((entry) => folderEntry(entry, spec)),
        items,
        total: page.total ?? items.length,
        hasMore: page.has_more === true,
      };
    },
    async allFolders() {
      const response = await client.listObjects<Record<string, unknown>>(spec.folder_object_type, { filter: { ...(spec.scope_filter ?? {}), status: { eq: 'active' } } as never, limit: 500 });
      return response.data.map((raw) => ({ id: String(raw['id']), name: string(raw[spec.name_field ?? 'name']) ?? '', parent_id: string(raw[spec.parent_field]) ?? null, locked: spec.locked_field !== undefined && raw[spec.locked_field] !== undefined && raw[spec.locked_field] !== null }));
    },
    itemsPayload(target) {
      if ('ids' in target) return { [idsField]: [...target.ids] };
      const parameters = listingParameters(spec, target.request);
      delete parameters['sort'];
      return { selection: { ...parameters, expected_count: target.total } };
    },
  };
}

/** Adaptateur de l'Object API : dossiers du type déclaré, éléments du type de la vue filtrés par dossier. */
export function objectFolderSource<Item>(client: ApiClient, spec: FolderSpec, itemType: string): FolderSource<Item> {
  const idsField = spec.commands?.move_items?.ids_field ?? 'ids';
  const scope = (spec.scope_filter ?? {}) as Record<string, Record<string, string>>;
  return {
    resolvesSelection: false,
    async list(request) {
      const where = request.folderId === null ? { exists: 'false' } : { eq: request.folderId };
      const filters = Object.fromEntries(Object.entries(request.filters).filter(([, value]) => value !== '').map(([field, value]) => [field, { eq: value }]));
      const searching = request.q !== '';
      const [folders, items] = await Promise.all([
        searching || request.all === true ? Promise.resolve({ data: [] as Array<Record<string, unknown>> }) : client.listObjects<Record<string, unknown>>(spec.folder_object_type, { filter: { ...scope, [spec.parent_field]: where } as never, limit: 500 }),
        client.listObjects<Item>(itemType, { filter: { ...filters, ...(searching || request.all === true || (request.q !== '' && request.scope === 'everywhere') ? {} : { [spec.item_folder_field]: where }) } as never, ...(searching ? { q: request.q } : {}), limit: 200, include_total: true }),
      ]);
      const path: Array<{ id: string; name: string }> = [];
      let current = request.folderId;
      for (let depth = 0; current !== null && depth < 32; depth += 1) {
        const read = await client.getObject<Record<string, unknown>>(spec.folder_object_type, current);
        path.unshift({ id: current, name: string(read.data[spec.name_field ?? 'name']) ?? '' });
        current = string(read.data[spec.parent_field]) ?? null;
      }
      const folder = request.folderId === null ? null : folderEntry({ id: request.folderId, [spec.name_field ?? 'name']: path.at(-1)?.name ?? '' }, spec);
      return { folder, path, folders: folders.data.map((raw) => folderEntry(raw, spec)), items: items.data, total: items.page?.total ?? items.data.length, hasMore: items.page?.next_cursor !== undefined };
    },
    async allFolders() {
      const response = await client.listObjects<Record<string, unknown>>(spec.folder_object_type, { filter: scope as never, limit: 500 });
      return response.data.map((raw) => ({ id: String(raw['id']), name: string(raw[spec.name_field ?? 'name']) ?? '', parent_id: string(raw[spec.parent_field]) ?? null, locked: spec.locked_field !== undefined && raw[spec.locked_field] !== undefined && raw[spec.locked_field] !== null }));
    },
    itemsPayload(target) {
      // L'Object API ne résout pas ici l'ensemble filtré pour une commande à identifiants : la page connue seulement.
      return { [idsField]: 'ids' in target ? [...target.ids] : [] };
    },
  };
}

/** Lecteur d'une source selon la déclaration : la requête si elle est déclarée, sinon l'Object API. */
export function folderSourceOf<Item>(client: ApiClient, spec: FolderSpec, itemType: string): FolderSource<Item> {
  return spec.listing_query !== undefined ? queryFolderSource<Item>(client, spec) : objectFolderSource<Item>(client, spec, itemType);
}

/**
 * Commandes déclarées de la source (ADR-149 §4) : la charge utile est faite des champs nommés par la
 * vue. Le serveur refuse un cycle, un dossier verrouillé, un nom déjà pris.
 */
export function folderCommands(client: ApiClient, spec: FolderSpec, source: Pick<FolderSource<unknown>, 'itemsPayload'>) {
  const commands = spec.commands ?? {};
  const folderTarget = (folder: FolderEntry) => ({ type: spec.folder_object_type, id: folder.id });
  return {
    can: { create: commands.create !== undefined, rename: commands.rename !== undefined, move: commands.move !== undefined, archive: commands.archive !== undefined, moveItems: commands.move_items !== undefined },
    create: (name: string, parentId: string | null) =>
      client.command(commands.create!.command_name, { payload: { ...(commands.create!.payload ?? {}), [commands.create!.name_field]: name, ...(parentId === null || commands.create!.parent_field === undefined ? {} : { [commands.create!.parent_field]: parentId }) } }),
    rename: (folder: FolderEntry, name: string) => client.command(commands.rename!.command_name, { payload: { [commands.rename!.name_field]: name }, target: folderTarget(folder) }),
    move: (folder: FolderEntry, parentId: string | null) => client.command(commands.move!.command_name, { payload: parentId === null ? {} : { [commands.move!.parent_field]: parentId }, target: folderTarget(folder) }),
    archive: (folder: FolderEntry, payload: Record<string, unknown> = {}) => client.command(commands.archive!.command_name, { payload, target: folderTarget(folder) }),
    moveItems: (target: { ids: readonly string[] } | { request: ListingRequest; total: number }, folderId: string | null) =>
      client.command(commands.move_items!.command_name, { payload: { ...source.itemsPayload(target), ...(folderId === null ? {} : { [commands.move_items!.folder_field]: folderId }) } }),
    runItems: (commandName: string, target: { ids: readonly string[] } | { request: ListingRequest; total: number }) => client.command(commandName, { payload: source.itemsPayload(target) }),
  };
}

export type FolderCommands = ReturnType<typeof folderCommands>;
